import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { SearchableSelect } from '../components/ui/searchable-select';
import { Checkbox } from '../components/ui/checkbox';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { IntegerQuantityInput } from '../components/IntegerQuantityInput';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { SortableTableHead } from '../components/ui/SortableTableHead';
import { useSortableTable } from '../hooks/useSortableTable';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Loader2, Package, Plus, Download, Trash2, Eye, Warehouse, Truck,
  RefreshCw, FileSpreadsheet, AlertCircle, AlertTriangle,
  TrendingUp, Target, Zap, ShoppingCart, Save, Info,
  ArrowRightLeft, ArrowUpDown, ExternalLink, ClipboardCheck,
} from 'lucide-react';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '../components/ui/tooltip';
import { toast } from 'sonner';
import { replenishmentAPI, predictiveRestockAPI } from '../lib/v2Api';
import { reorderAPI, warehouseAPI, vansAPI } from '../lib/api';
import { format, formatDistanceToNow } from 'date-fns';
import { MobileFAB } from '../components/MobileFAB';

const formatCurrency = (value) => {
  if (value === null || value === undefined) return '-';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
};

// ──────────────────────────────────────────────
// Tab 1: Needs Attention Now (absorbed from /reorder)
// ──────────────────────────────────────────────
const NeedsAttentionPanel = ({ onCountChange }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const [restockItem, setRestockItem] = useState(null);
  const [restockQty, setRestockQty] = useState('');
  const [restocking, setRestocking] = useState(false);

  const [transferItem, setTransferItem] = useState(null);
  const [transferQty, setTransferQty] = useState('');
  const [transferring, setTransferring] = useState(false);

  const fetchReorderList = useCallback(async () => {
    try {
      setLoading(true);
      const response = await reorderAPI.list();
      setItems(response.data);
      onCountChange?.(response.data.length);
    } catch (error) {
      toast.error('Failed to load reorder list');
    } finally {
      setLoading(false);
    }
  }, [onCountChange]);

  useEffect(() => { fetchReorderList(); }, [fetchReorderList]);

  const groupedItems = useMemo(() => {
    const warehouse = items.filter(i => i.location.type === 'warehouse');
    const fleet = items.filter(i => i.location.type === 'fleet');
    const byVehicle = {};
    items.filter(i => i.location.type === 'vehicle').forEach(item => {
      const vehicleId = item.location.vehicle_id;
      if (!byVehicle[vehicleId]) {
        byVehicle[vehicleId] = { vehicle_name: item.location.vehicle_name, items: [] };
      }
      byVehicle[vehicleId].items.push(item);
    });
    return { warehouse, fleet, vehicles: byVehicle };
  }, [items]);

  const handleRestock = async () => {
    if (!restockItem || !restockQty || parseInt(restockQty) <= 0) return;
    setRestocking(true);
    try {
      if (restockItem.location.type === 'warehouse') {
        await warehouseAPI.restock({ item_id: restockItem.item_id, quantity: parseInt(restockQty) });
        toast.success(`Restocked ${restockQty} units in warehouse`);
      } else if (restockItem.location.type === 'fleet') {
        toast.error('Fleet restock not implemented yet - use Import');
        setRestocking(false);
        return;
      } else {
        await vansAPI.restock(restockItem.location.vehicle_id, { item_id: restockItem.item_id, quantity: parseInt(restockQty) });
        toast.success(`Restocked ${restockQty} units`);
      }
      setRestockItem(null);
      setRestockQty('');
      fetchReorderList();
    } catch (error) {
      toast.error('Failed to restock');
    } finally {
      setRestocking(false);
    }
  };

  const handleTransfer = async () => {
    if (!transferItem || !transferQty || parseInt(transferQty) <= 0) return;
    const qty = parseInt(transferQty);
    const maxAvailable = Math.max(transferItem.fleet_qty_available || 0, transferItem.warehouse_qty_available || 0);
    if (qty > maxAvailable) {
      toast.error(`Only ${maxAvailable} available for transfer`);
      return;
    }
    setTransferring(true);
    try {
      if (transferItem.warehouse_qty_available && transferItem.warehouse_qty_available >= qty) {
        await warehouseAPI.transfer({
          from_location_type: 'warehouse', from_location_id: 'default',
          to_location_type: 'vehicle', to_location_id: transferItem.location.vehicle_id,
          item_id: transferItem.item_id, quantity: qty, note: 'Low stock transfer from warehouse'
        });
        toast.success(`Transferred ${qty} units from Warehouse`);
      } else {
        await vansAPI.transfer({
          item_id: transferItem.item_id, from_van_id: null,
          to_van_id: transferItem.location.vehicle_id, quantity: qty
        });
        toast.success(`Transferred ${qty} units from Fleet`);
      }
      setTransferItem(null);
      setTransferQty('');
      fetchReorderList();
    } catch (error) {
      toast.error('Failed to transfer');
    } finally {
      setTransferring(false);
    }
  };

  const renderItemRow = (item) => {
    const hasWarehouseStock = item.warehouse_qty_available && item.warehouse_qty_available > 0;
    const hasFleetStock = item.fleet_qty_available && item.fleet_qty_available > 0;
    const transferAvailable = hasWarehouseStock || hasFleetStock;
    const transferQtyMax = Math.max(item.warehouse_qty_available || 0, item.fleet_qty_available || 0);

    return (
      <div key={`${item.item_id}-${item.location.vehicle_id || item.location.type}`} className="p-4 border rounded-lg" data-testid={`reorder-item-${item.item_id}`}>
        {/* Desktop */}
        <div className="hidden md:block space-y-3">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h4 className="font-semibold text-foreground truncate">{item.item_name}</h4>
                {item.category_names?.length > 0 && (
                  <span className="text-xs px-2 py-0.5 bg-primary/10 text-primary rounded-full">
                    {item.category_names.join(', ')}
                  </span>
                )}
                {item.external_url && (
                  <a href={item.external_url} target="_blank" rel="noopener noreferrer"
                    className="text-xs font-medium px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full inline-flex items-center gap-1 hover:bg-blue-200 transition-colors"
                    onClick={(e) => e.stopPropagation()} title={item.external_url}>
                    <ExternalLink className="w-3 h-3" /> Supply House
                  </a>
                )}
              </div>
              {item.sku && <p className="text-sm text-muted-foreground font-mono">{item.sku}</p>}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className="text-sm font-mono bg-destructive/10 text-destructive px-2 py-1 rounded">
                {item.quantity} / {item.reorder_threshold}
              </span>
              <span className="text-sm font-bold text-destructive">-{item.shortage}</span>
            </div>
          </div>
          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <div className="space-x-4">
              <span>Unit: {formatCurrency(item.unit_cost)}</span>
              <span>Billable: {formatCurrency(item.billable_price)}</span>
            </div>
            {item.cost_to_reorder && <span className="font-medium">Reorder cost: {formatCurrency(item.cost_to_reorder)}</span>}
          </div>
          <div className="flex items-center gap-2 pt-2 border-t flex-wrap">
            <Button size="sm" onClick={() => { setRestockItem(item); setRestockQty(String(item.shortage)); }} data-testid={`restock-btn-${item.item_id}`}>
              <RefreshCw className="w-3 h-3 mr-1" /> Restock
            </Button>
            {item.location.type === 'vehicle' && !!transferAvailable && (
              <Button size="sm" variant="outline" onClick={() => { setTransferItem(item); setTransferQty(String(Math.min(item.shortage, transferQtyMax))); }}>
                <ArrowRightLeft className="w-3 h-3 mr-1" />
                {hasWarehouseStock ? `Transfer from Warehouse (${item.warehouse_qty_available})` : `Transfer from Fleet (${item.fleet_qty_available})`}
              </Button>
            )}
          </div>
        </div>

        {/* Mobile */}
        <div className="md:hidden space-y-3">
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-1.5 min-w-0 flex-1">
              <h4 className="font-semibold text-foreground truncate">{item.item_name}</h4>
              {item.external_url && (
                <a href={item.external_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 shrink-0" onClick={(e) => e.stopPropagation()}>
                  <ExternalLink className="w-4 h-4" />
                </a>
              )}
            </div>
            <span className="text-sm font-bold px-2 py-0.5 bg-destructive/10 text-destructive rounded-full shrink-0">-{item.shortage}</span>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
              {item.location.type === 'warehouse' ? <Warehouse className="w-3 h-3" /> : <Truck className="w-3 h-3" />}
              {item.location.type === 'warehouse' ? 'Warehouse' : (item.location.vehicle_name || 'Fleet')}
            </span>
            {item.category_names?.length > 0 && <span className="text-xs px-2 py-0.5 bg-primary/10 text-primary rounded-full">{item.category_names.join(', ')}</span>}
            {item.sku && <span className="text-xs text-muted-foreground font-mono">#{item.sku}</span>}
          </div>
          <div className="flex items-center gap-3 text-sm">
            <span className="text-muted-foreground">{item.quantity} / {item.reorder_threshold}</span>
            <span className="inline-flex items-center gap-1 text-xs text-amber-600"><AlertTriangle className="w-3 h-3" /> Low</span>
          </div>
          <div className="text-xs text-muted-foreground space-y-0.5">
            <div>Unit: {formatCurrency(item.unit_cost)} · Billable: {formatCurrency(item.billable_price)}</div>
            {item.cost_to_reorder && <div className="text-foreground font-medium">Reorder: {formatCurrency(item.cost_to_reorder)}</div>}
          </div>
          <div className="flex flex-col gap-2 pt-2 border-t">
            <Button size="sm" className="w-full" onClick={() => { setRestockItem(item); setRestockQty(String(item.shortage)); }}>
              <ClipboardCheck className="w-4 h-4 mr-2" /> Restock
            </Button>
            {item.location.type === 'vehicle' && !!transferAvailable && (
              <Button size="sm" variant="outline" className="w-full" onClick={() => { setTransferItem(item); setTransferQty(String(Math.min(item.shortage, transferQtyMax))); }}>
                <ArrowRightLeft className="w-4 h-4 mr-2" /> Transfer from {hasWarehouseStock ? 'Warehouse' : 'Fleet'} ({transferQtyMax})
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Items currently below their reorder threshold — take action now.
          </p>
          <Button variant="outline" size="sm" onClick={fetchReorderList} disabled={loading}>
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : items.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Package className="w-12 h-12 text-green-500 mb-4" />
              <p className="text-lg font-semibold text-foreground">All stock levels healthy</p>
              <p className="text-muted-foreground mt-1 text-sm">No items are currently below their reorder threshold</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {groupedItems.warehouse.length > 0 && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-lg font-bold">
                    <Warehouse className="w-5 h-5 text-primary" /> Warehouse
                    <Badge variant="destructive" className="ml-auto text-xs">{groupedItems.warehouse.length}</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">{groupedItems.warehouse.map(renderItemRow)}</CardContent>
              </Card>
            )}
            {groupedItems.fleet.length > 0 && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-lg font-bold">
                    <Warehouse className="w-5 h-5" /> Fleet Inventory
                    <Badge variant="destructive" className="ml-auto text-xs">{groupedItems.fleet.length}</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">{groupedItems.fleet.map(renderItemRow)}</CardContent>
              </Card>
            )}
            {Object.entries(groupedItems.vehicles).map(([vehicleId, data]) => (
              <Card key={vehicleId}>
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-lg font-bold">
                    <Truck className="w-5 h-5" /> {data.vehicle_name}
                    <Badge variant="destructive" className="ml-auto text-xs">{data.items.length}</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">{data.items.map(renderItemRow)}</CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Restock Dialog */}
      <Dialog open={!!restockItem} onOpenChange={(open) => !open && setRestockItem(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Restock Item</DialogTitle>
            <DialogDescription>
              Add inventory to {restockItem?.location.type === 'fleet' ? 'Fleet' : restockItem?.location.vehicle_name || 'Warehouse'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <p className="font-semibold">{restockItem?.item_name}</p>
              <p className="text-sm text-muted-foreground">
                Current: {restockItem?.quantity} / Threshold: {restockItem?.reorder_threshold}
              </p>
            </div>
            <div className="space-y-2">
              <Label>Quantity to Restock</Label>
              <IntegerQuantityInput min="1" value={restockQty} onChange={(val) => setRestockQty(val)} placeholder="Enter quantity" data-testid="restock-qty-input" />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setRestockItem(null)}>Cancel</Button>
              <Button onClick={handleRestock} disabled={restocking || !restockQty} data-testid="restock-confirm-btn">
                {restocking && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Restock
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Transfer Dialog */}
      <Dialog open={!!transferItem} onOpenChange={(open) => !open && setTransferItem(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Transfer Stock</DialogTitle>
            <DialogDescription>Transfer inventory to {transferItem?.location.vehicle_name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <p className="font-semibold">{transferItem?.item_name}</p>
              <p className="text-sm text-muted-foreground">
                Available: {Math.max(transferItem?.warehouse_qty_available || 0, transferItem?.fleet_qty_available || 0)} | Shortage: {transferItem?.shortage}
              </p>
            </div>
            <div className="space-y-2">
              <Label>Quantity to Transfer</Label>
              <IntegerQuantityInput min="1" max={Math.max(transferItem?.warehouse_qty_available || 0, transferItem?.fleet_qty_available || 0)} value={transferQty} onChange={(val) => setTransferQty(val)} placeholder="Enter quantity" data-testid="transfer-qty-input" />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setTransferItem(null)}>Cancel</Button>
              <Button onClick={handleTransfer} disabled={transferring || !transferQty} data-testid="transfer-confirm-btn">
                {transferring && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Transfer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

// ──────────────────────────────────────────────
// Tab 2: Prediction Panel (preserved from original)
// ──────────────────────────────────────────────
const PredictionPanel = ({ warehouses, vehicles }) => {
  const [loading, setLoading] = useState(true);
  const [predictions, setPredictions] = useState([]);
  const [mode, setMode] = useState('hybrid');
  const [sourceType, setSourceType] = useState('');
  const [sourceId, setSourceId] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');

  const fetchPredictions = useCallback(async () => {
    setLoading(true);
    try {
      const params = { mode };
      if (sourceType) params.source_type = sourceType;
      if (sourceId) params.source_id = sourceId;
      const res = await predictiveRestockAPI.getPlan(params);
      setPredictions(res.data || []);
      setSelected(new Set());
    } catch (err) {
      console.error('Failed to fetch predictions:', err);
      toast.error('Failed to load predictions');
    } finally {
      setLoading(false);
    }
  }, [mode, sourceType, sourceId]);

  useEffect(() => { fetchPredictions(); }, [fetchPredictions]);

  const filtered = useMemo(() => {
    if (filterStatus === 'all') return predictions;
    if (filterStatus === 'restock') return predictions.filter(p => p.status === 'RESTOCK_NEEDED');
    return predictions.filter(p => p.status === 'OK');
  }, [predictions, filterStatus]);

  const PRED_SORT_COLS = [
    { key: 'item_name', type: 'text' }, { key: 'source_name', type: 'text' },
    { key: 'qty_on_hand', type: 'number' }, { key: 'avg_daily_usage', type: 'number' },
    { key: 'days_until_empty', type: 'number' }, { key: 'target_qty', type: 'number' },
    { key: 'suggested_restock_qty', type: 'number' }, { key: 'status', type: 'status' },
  ];
  const { sorted: sortedPredictions, sortKey: predSortKey, sortDir: predSortDir, onSort: predOnSort } = useSortableTable(filtered, { columns: PRED_SORT_COLS });

  const toggleSelect = (key) => {
    setSelected(prev => { const next = new Set(prev); if (next.has(key)) next.delete(key); else next.add(key); return next; });
  };
  const toggleAll = () => {
    const restockItems = filtered.filter(p => p.suggested_restock_qty > 0);
    if (selected.size === restockItems.length) setSelected(new Set());
    else setSelected(new Set(restockItems.map(p => `${p.item_id}|${p.source_type}|${p.source_id}`)));
  };
  const selectedItems = useMemo(() => predictions.filter(p => selected.has(`${p.item_id}|${p.source_type}|${p.source_id}`)), [predictions, selected]);
  const cartTotal = useMemo(() => selectedItems.reduce((sum, p) => sum + p.suggested_restock_qty, 0), [selectedItems]);
  const cartCost = useMemo(() => selectedItems.reduce((sum, p) => sum + (p.estimated_cost || 0), 0), [selectedItems]);

  const handleSave = async () => {
    if (selectedItems.length === 0) return;
    setSaving(true);
    try {
      const res = await predictiveRestockAPI.savePlan({
        items: selectedItems.map(p => ({ item_id: p.item_id, source_type: p.source_type, source_id: p.source_id, suggested_qty: p.suggested_restock_qty })),
      });
      toast.success(`Saved restock list: ${res.data.total_items} items, ${res.data.total_suggested_qty} units`);
      setSelected(new Set());
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save plan');
    } finally {
      setSaving(false);
    }
  };

  const modeOptions = [
    { value: 'hybrid', label: 'Hybrid', icon: Zap, tooltip: 'Uses whichever restock suggestion is higher: usage-based or target-based.' },
    { value: 'velocity', label: 'Velocity', icon: TrendingUp, tooltip: 'Restock based on how quickly this item has been used recently.' },
    { value: 'target', label: 'Target', icon: Target, tooltip: 'Restock up to the stock quantity you manually set.' },
  ];
  const restockCount = predictions.filter(p => p.status === 'RESTOCK_NEEDED').length;

  return (
    <div className="space-y-4" data-testid="prediction-panel">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Intelligent restock recommendations based on usage analytics and stock targets.
        </p>
        <Button variant="outline" size="sm" onClick={fetchPredictions} disabled={loading}>
          <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3" data-testid="prediction-filters">
        <div className="space-y-1">
          <label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
            Mode
            <TooltipProvider delayDuration={200}><Tooltip><TooltipTrigger asChild><Info className="w-3 h-3 text-muted-foreground cursor-help" /></TooltipTrigger>
              <TooltipContent side="top" className="max-w-xs text-xs leading-relaxed">
                <p className="font-semibold mb-1">Prediction Modes</p>
                <p><span className="font-medium">Velocity:</span> Based on recent usage speed</p>
                <p><span className="font-medium">Target:</span> Based on your manual stock targets</p>
                <p><span className="font-medium">Hybrid:</span> Whichever suggestion is higher</p>
              </TooltipContent>
            </Tooltip></TooltipProvider>
          </label>
          <div className="flex gap-1">
            {modeOptions.map(opt => {
              const Icon = opt.icon;
              return (
                <TooltipProvider key={opt.value} delayDuration={300}><Tooltip><TooltipTrigger asChild>
                  <Button size="sm" variant={mode === opt.value ? 'default' : 'outline'} onClick={() => setMode(opt.value)} data-testid={`mode-${opt.value}`} className="text-xs">
                    <Icon className="w-3.5 h-3.5 mr-1" /> {opt.label}
                  </Button>
                </TooltipTrigger><TooltipContent side="bottom" className="max-w-[200px]">{opt.tooltip}</TooltipContent></Tooltip></TooltipProvider>
              );
            })}
          </div>
        </div>
        <div className="space-y-1 min-w-[140px]">
          <label className="text-xs font-medium text-muted-foreground">Location Type</label>
          <SearchableSelect options={[{ value: '', label: 'All Locations' }, { value: 'vehicle', label: 'Vehicles' }, { value: 'warehouse', label: 'Warehouses' }]}
            value={sourceType} onValueChange={(v) => { setSourceType(v); setSourceId(''); }} placeholder="All" sortAlphabetically={false} />
        </div>
        {sourceType && (
          <div className="space-y-1 min-w-[180px]">
            <label className="text-xs font-medium text-muted-foreground">{sourceType === 'vehicle' ? 'Vehicle' : 'Warehouse'}</label>
            <SearchableSelect options={[{ value: '', label: `All ${sourceType === 'vehicle' ? 'Vehicles' : 'Warehouses'}` }, ...(sourceType === 'vehicle' ? vehicles : warehouses).map(l => ({ value: l.id, label: l.name }))]}
              value={sourceId} onValueChange={setSourceId} placeholder={`Select ${sourceType}...`} />
          </div>
        )}
        <div className="space-y-1 min-w-[130px]">
          <label className="text-xs font-medium text-muted-foreground">Status</label>
          <SearchableSelect options={[{ value: 'all', label: `All (${predictions.length})` }, { value: 'restock', label: `Restock (${restockCount})` }, { value: 'ok', label: `OK (${predictions.length - restockCount})` }]}
            value={filterStatus} onValueChange={setFilterStatus} sortAlphabetically={false} />
        </div>
      </div>

      {/* Cart Bar */}
      {selected.size > 0 && (
        <div className="flex items-center justify-between px-4 py-2.5 bg-indigo-50 border border-indigo-200 rounded-lg" data-testid="restock-cart">
          <div className="flex items-center gap-3">
            <ShoppingCart className="w-4 h-4 text-indigo-600" />
            <span className="text-sm font-medium text-indigo-800">{selected.size} item{selected.size !== 1 ? 's' : ''} selected</span>
            <Badge variant="secondary">{cartTotal} units</Badge>
            {cartCost > 0 && <Badge variant="outline">${cartCost.toFixed(2)} est.</Badge>}
          </div>
          <Button size="sm" onClick={handleSave} disabled={saving} data-testid="save-restock-plan-btn">
            {saving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1.5" />} Save Restock List
          </Button>
        </div>
      )}

      {/* Predictions Table */}
      {loading ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-10 text-muted-foreground">
          <Package className="w-10 h-10 mx-auto mb-3 opacity-50" />
          <p className="font-medium">No items to show</p>
          <p className="text-xs mt-1">{predictions.length === 0 ? 'No inventory data found' : 'All items match the current filter'}</p>
        </div>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <Table data-testid="predictions-table">
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead className="w-10"><Checkbox checked={selected.size > 0 && selected.size === filtered.filter(p => p.suggested_restock_qty > 0).length} onCheckedChange={toggleAll} data-testid="select-all-checkbox" /></TableHead>
                <SortableTableHead sortKey={predSortKey} sortDir={predSortDir} columnKey="item_name" onSort={predOnSort}>Item</SortableTableHead>
                <SortableTableHead sortKey={predSortKey} sortDir={predSortDir} columnKey="source_name" onSort={predOnSort}>Location</SortableTableHead>
                <SortableTableHead sortKey={predSortKey} sortDir={predSortDir} columnKey="qty_on_hand" onSort={predOnSort} className="text-right">On Hand</SortableTableHead>
                <SortableTableHead sortKey={predSortKey} sortDir={predSortDir} columnKey="avg_daily_usage" onSort={predOnSort} className="text-right">Usage/Day</SortableTableHead>
                <SortableTableHead sortKey={predSortKey} sortDir={predSortDir} columnKey="days_until_empty" onSort={predOnSort} className="text-right">Days Left</SortableTableHead>
                <SortableTableHead sortKey={predSortKey} sortDir={predSortDir} columnKey="target_qty" onSort={predOnSort} className="text-right">Target</SortableTableHead>
                <SortableTableHead sortKey={predSortKey} sortDir={predSortDir} columnKey="suggested_restock_qty" onSort={predOnSort} className="text-right">Suggest</SortableTableHead>
                <SortableTableHead sortKey={predSortKey} sortDir={predSortDir} columnKey="status" onSort={predOnSort}>Status</SortableTableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedPredictions.map((row) => {
                const key = `${row.item_id}|${row.source_type}|${row.source_id}`;
                const isSelected = selected.has(key);
                const isRestock = row.status === 'RESTOCK_NEEDED';
                const SrcIcon = row.source_type === 'vehicle' ? Truck : Warehouse;
                return (
                  <TableRow key={key} className={isSelected ? 'bg-indigo-50/50' : ''} data-testid={`prediction-row-${row.item_id}`}>
                    <TableCell><Checkbox checked={isSelected} disabled={row.suggested_restock_qty === 0} onCheckedChange={() => toggleSelect(key)} /></TableCell>
                    <TableCell><div><p className="font-medium text-sm">{row.item_name}</p>{row.item_sku && <p className="text-xs text-muted-foreground">{row.item_sku}</p>}</div></TableCell>
                    <TableCell><div className="flex items-center gap-1.5"><SrcIcon className="w-3.5 h-3.5 text-muted-foreground" /><span className="text-xs">{row.source_name}</span></div></TableCell>
                    <TableCell className="text-right font-mono text-sm">{row.qty_on_hand}</TableCell>
                    <TableCell className="text-right font-mono text-sm">{row.avg_daily_usage > 0 ? row.avg_daily_usage.toFixed(1) : '\u2014'}</TableCell>
                    <TableCell className="text-right font-mono text-sm">{row.days_until_empty !== null ? <span className={row.days_until_empty < 7 ? 'text-red-600 font-semibold' : ''}>{row.days_until_empty}d</span> : '\u2014'}</TableCell>
                    <TableCell className="text-right font-mono text-sm">{row.target_qty !== null ? row.target_qty : '\u2014'}</TableCell>
                    <TableCell className="text-right">{row.suggested_restock_qty > 0 ? <span className="font-semibold text-indigo-700">{row.suggested_restock_qty}</span> : <span className="text-muted-foreground">0</span>}</TableCell>
                    <TableCell><Badge variant={isRestock ? 'destructive' : 'secondary'} className="text-[10px]">{isRestock ? 'RESTOCK' : 'OK'}</Badge></TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
      {!loading && predictions.length > 0 && (
        <div className="flex items-center gap-4 text-xs text-muted-foreground pt-1">
          <span>{predictions.length} items scanned</span>
          <span className="text-red-600 font-medium">{restockCount} need restocking</span>
          <span className="ml-auto italic">Mode: <span className="font-medium capitalize">{mode}</span></span>
        </div>
      )}
    </div>
  );
};

// ──────────────────────────────────────────────
// Tab 3: Saved Lists Panel (preserved from original)
// ──────────────────────────────────────────────
const SavedListsPanel = ({ lists, loading, warehouses, vehicles, onRefresh }) => {
  const [exporting, setExporting] = useState(null);
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  const [locationType, setLocationType] = useState('warehouse');
  const [locationId, setLocationId] = useState('');
  const [includeThresholdOnly, setIncludeThresholdOnly] = useState(true);
  const [generating, setGenerating] = useState(false);

  const [selectedList, setSelectedList] = useState(null);
  const [listDetail, setListDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const [applyTarget, setApplyTarget] = useState(null);
  const [applyWarehouseId, setApplyWarehouseId] = useState('');
  const [applyVanId, setApplyVanId] = useState('');
  const [applying, setApplying] = useState(false);
  const [selectedLineIds, setSelectedLineIds] = useState(new Set());

  const handleGenerate = async () => {
    try {
      setGenerating(true);
      const data = { location_type: locationType, location_id: (locationType === 'warehouse' || locationType === 'vehicle') ? locationId : null, include_below_threshold_only: includeThresholdOnly };
      const response = await replenishmentAPI.generate(data);
      toast.success('Replenishment list generated');
      setShowGenerateDialog(false);
      onRefresh();
      if (response.data.total_items > 0) { setSelectedList(response.data); setListDetail(response.data); }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate list');
    } finally { setGenerating(false); }
  };

  const handleExport = async (list) => {
    try {
      setExporting(list.id);
      const response = await replenishmentAPI.export(list.id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `replenishment_${list.location_name || 'list'}_${format(new Date(), 'yyyyMMdd')}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CSV exported');
    } catch (error) { toast.error('Failed to export CSV'); }
    finally { setExporting(null); }
  };

  const handleViewDetail = async (list) => {
    setSelectedList(list);
    setLoadingDetail(true);
    setSelectedLineIds(new Set());
    try {
      const response = await replenishmentAPI.get(list.id);
      setListDetail(response.data);
    } catch (error) { toast.error('Failed to load list details'); }
    finally { setLoadingDetail(false); }
  };

  const handleDelete = async (list) => {
    if (!confirm('Delete this replenishment list?')) return;
    try { await replenishmentAPI.delete(list.id); toast.success('List deleted'); onRefresh(); }
    catch (error) { toast.error('Failed to delete list'); }
  };

  const openApplyDialog = (list, lineIds = null) => {
    setApplyTarget(list);
    setApplyWarehouseId('');
    setApplyVanId('');
    if (lineIds) setSelectedLineIds(new Set(lineIds));
  };

  const handleApply = async () => {
    if (!applyTarget || !applyWarehouseId || !applyVanId) return;
    setApplying(true);
    try {
      const payload = { source_warehouse_id: applyWarehouseId, destination_van_id: applyVanId };
      if (selectedLineIds.size > 0) payload.line_ids = Array.from(selectedLineIds);
      const res = await replenishmentAPI.apply(applyTarget.id, payload);
      const d = res.data;
      if (d.errors?.length > 0) toast.warning(`${d.lines_applied} applied, ${d.errors.length} failed: ${d.errors[0]}`);
      else toast.success(d.message);
      setApplyTarget(null);
      setSelectedLineIds(new Set());
      onRefresh();
      if (selectedList) handleViewDetail(selectedList);
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to apply replenishment list'); }
    finally { setApplying(false); }
  };

  const toggleLineSelect = (lineId) => {
    setSelectedLineIds(prev => { const next = new Set(prev); if (next.has(lineId)) next.delete(lineId); else next.add(lineId); return next; });
  };
  const toggleAllLines = () => {
    if (!listDetail) return;
    const draftLines = listDetail.items.filter(i => i.line_status !== 'applied' && i.suggested_qty > 0);
    if (selectedLineIds.size === draftLines.length) setSelectedLineIds(new Set());
    else setSelectedLineIds(new Set(draftLines.map(i => i.id)));
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'applied': return <Badge className="text-[10px] bg-green-100 text-green-700 border-green-200">Applied</Badge>;
      case 'partial': return <Badge className="text-[10px] bg-amber-100 text-amber-700 border-amber-200">Partial</Badge>;
      case 'exported': return <Badge className="text-[10px] bg-blue-100 text-blue-700 border-blue-200">Exported</Badge>;
      case 'draft': return <Badge variant="outline" className="text-[10px]">Draft</Badge>;
      default: return <Badge variant="secondary" className="text-[10px]">{status}</Badge>;
    }
  };
  const getLocationIcon = (type) => (type === 'warehouse' || type === 'all_warehouses') ? Warehouse : Truck;

  return (
    <>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">Previously generated replenishment lists.</p>
          <Button onClick={() => setShowGenerateDialog(true)} data-testid="generate-list-btn" size="sm">
            <Plus className="w-4 h-4 mr-1.5" /> Generate List
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
        ) : lists.length === 0 ? (
          <div className="text-center py-12">
            <FileSpreadsheet className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="font-medium text-lg">No replenishment lists yet</h3>
            <p className="text-sm text-muted-foreground mt-1">Generate a list or save a predicted plan from the Predictions tab</p>
          </div>
        ) : (
          <div className="border rounded-lg overflow-hidden">
            <Table data-testid="saved-lists-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Location</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Items</TableHead>
                  <TableHead>Suggested Qty</TableHead>
                  <TableHead>Est. Cost</TableHead>
                  <TableHead>Generated</TableHead>
                  <TableHead className="w-[180px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {lists.map((list) => {
                  const Icon = getLocationIcon(list.location_type);
                  const isFullyApplied = list.status === 'applied';
                  const canApply = !isFullyApplied && list.total_items > 0;
                  return (
                    <TableRow key={list.id} data-testid={`list-row-${list.id}`}>
                      <TableCell><div className="flex items-center gap-2"><Icon className="w-4 h-4 text-muted-foreground" /><span className="font-medium">{list.location_name}</span></div></TableCell>
                      <TableCell>{getStatusBadge(list.status)}</TableCell>
                      <TableCell><Badge variant={list.total_items > 0 ? 'default' : 'secondary'}>{list.total_items} items</Badge></TableCell>
                      <TableCell>{list.total_suggested_qty}</TableCell>
                      <TableCell>{list.total_estimated_cost ? `$${Number(list.total_estimated_cost).toFixed(2)}` : '\u2014'}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{list.generated_at ? formatDistanceToNow(new Date(list.generated_at), { addSuffix: true }) : '\u2014'}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {canApply && (
                            <Button size="sm" variant="outline" onClick={() => openApplyDialog(list)} className="text-xs text-green-700 border-green-200 hover:bg-green-50">
                              <ArrowUpDown className="w-3.5 h-3.5 mr-1" /> Apply
                            </Button>
                          )}
                          {isFullyApplied && <span className="text-xs text-green-600 px-2 py-1 font-medium">Applied {list.applied_at ? formatDistanceToNow(new Date(list.applied_at), { addSuffix: true }) : ''}</span>}
                          <Button size="sm" variant="ghost" onClick={() => handleViewDetail(list)}><Eye className="w-4 h-4" /></Button>
                          <Button size="sm" variant="ghost" onClick={() => handleExport(list)} disabled={exporting === list.id || list.total_items === 0}>
                            {exporting === list.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => handleDelete(list)} disabled={isFullyApplied}><Trash2 className="w-4 h-4 text-destructive" /></Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Generate Dialog */}
      <Dialog open={showGenerateDialog} onOpenChange={setShowGenerateDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Generate Replenishment List</DialogTitle><DialogDescription>Create a list of items that need restocking based on par levels.</DialogDescription></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Location Type</label>
              <SearchableSelect options={[{ value: 'warehouse', label: 'Single Warehouse' }, { value: 'vehicle', label: 'Single Vehicle' }, { value: 'all_warehouses', label: 'All Warehouses' }, { value: 'all_vehicles', label: 'All Vehicles' }]}
                value={locationType} onValueChange={(v) => { setLocationType(v); setLocationId(''); }} placeholder="Select location type" sortAlphabetically={false} />
            </div>
            {(locationType === 'warehouse' || locationType === 'vehicle') && (
              <div className="space-y-2">
                <label className="text-sm font-medium">{locationType === 'warehouse' ? 'Warehouse' : 'Vehicle'}</label>
                <SearchableSelect options={(locationType === 'warehouse' ? warehouses : vehicles).map(loc => ({ value: loc.id, label: loc.name }))}
                  value={locationId} onValueChange={setLocationId} placeholder={`Select ${locationType}...`} emptyText={`No ${locationType}s found.`} />
              </div>
            )}
            <div className="flex items-center gap-2">
              <input type="checkbox" id="threshold-only" checked={includeThresholdOnly} onChange={(e) => setIncludeThresholdOnly(e.target.checked)} className="rounded" />
              <label htmlFor="threshold-only" className="text-sm">Only include items below threshold</label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGenerateDialog(false)}>Cancel</Button>
            <Button onClick={handleGenerate} disabled={generating || ((locationType === 'warehouse' || locationType === 'vehicle') && !locationId)} data-testid="generate-confirm-btn">
              {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />} Generate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={!!selectedList} onOpenChange={() => { setSelectedList(null); setListDetail(null); setSelectedLineIds(new Set()); }}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Package className="w-5 h-5" /> {selectedList?.location_name} {listDetail && <span className="ml-2">{getStatusBadge(listDetail.status)}</span>}</DialogTitle>
            <DialogDescription>Generated {selectedList?.generated_at ? format(new Date(selectedList.generated_at), 'PPp') : ''}{listDetail?.applied_by_name && ` | Applied by ${listDetail.applied_by_name}`}</DialogDescription>
          </DialogHeader>
          {loadingDetail ? (
            <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" /></div>
          ) : listDetail ? (
            <div className="flex-1 overflow-auto">
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="p-3 bg-muted rounded-lg text-center"><p className="text-2xl font-bold">{listDetail.total_items}</p><p className="text-xs text-muted-foreground">Items</p></div>
                <div className="p-3 bg-muted rounded-lg text-center"><p className="text-2xl font-bold">{listDetail.total_suggested_qty}</p><p className="text-xs text-muted-foreground">Units Needed</p></div>
                <div className="p-3 bg-muted rounded-lg text-center"><p className="text-2xl font-bold">{listDetail.total_estimated_cost ? `$${Number(listDetail.total_estimated_cost).toFixed(0)}` : '\u2014'}</p><p className="text-xs text-muted-foreground">Est. Cost</p></div>
              </div>
              {selectedLineIds.size > 0 && listDetail.status !== 'applied' && (
                <div className="flex items-center justify-between px-4 py-2 mb-3 bg-green-50 border border-green-200 rounded-lg">
                  <span className="text-sm text-green-800 font-medium">{selectedLineIds.size} line{selectedLineIds.size !== 1 ? 's' : ''} selected</span>
                  <Button size="sm" onClick={() => openApplyDialog(listDetail, Array.from(selectedLineIds))} className="bg-green-600 hover:bg-green-700 text-xs"><ArrowUpDown className="w-3.5 h-3.5 mr-1" /> Apply Selected</Button>
                </div>
              )}
              {listDetail.items?.length > 0 ? (
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        {listDetail.status !== 'applied' && (
                          <TableHead className="w-10"><Checkbox checked={selectedLineIds.size > 0 && selectedLineIds.size === listDetail.items.filter(i => i.line_status !== 'applied' && i.suggested_qty > 0).length} onCheckedChange={toggleAllLines} /></TableHead>
                        )}
                        <TableHead>Item</TableHead><TableHead className="text-right">On Hand</TableHead><TableHead className="text-right">Par Level</TableHead><TableHead className="text-right">Suggested</TableHead><TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {listDetail.items.map(item => {
                        const isLineApplied = item.line_status === 'applied';
                        const isSelected = selectedLineIds.has(item.id);
                        return (
                          <TableRow key={item.id} className={isLineApplied ? 'bg-green-50/50 opacity-75' : isSelected ? 'bg-indigo-50/50' : ''}>
                            {listDetail.status !== 'applied' && <TableCell><Checkbox checked={isSelected} disabled={isLineApplied || item.suggested_qty === 0} onCheckedChange={() => toggleLineSelect(item.id)} /></TableCell>}
                            <TableCell><div><p className="font-medium">{item.item_name}</p>{item.item_sku && <p className="text-xs text-muted-foreground">{item.item_sku}</p>}</div></TableCell>
                            <TableCell className="text-right">{item.qty_on_hand}</TableCell>
                            <TableCell className="text-right">{item.par_level || '\u2014'}</TableCell>
                            <TableCell className="text-right font-medium">{item.suggested_qty}</TableCell>
                            <TableCell>{isLineApplied ? <Badge className="text-[10px] bg-green-100 text-green-700 border-green-200">Applied</Badge> : <Badge variant="outline" className="text-[10px]">Draft</Badge>}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground"><AlertCircle className="w-8 h-8 mx-auto mb-2" /><p>All items are at or above par level</p></div>
              )}
            </div>
          ) : null}
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => { setSelectedList(null); setSelectedLineIds(new Set()); }}>Close</Button>
            {listDetail?.total_items > 0 && (
              <Button onClick={() => handleExport(listDetail)} disabled={exporting === listDetail?.id}>
                {exporting === listDetail?.id ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />} Export CSV
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Apply Confirmation Dialog */}
      <Dialog open={!!applyTarget} onOpenChange={() => setApplyTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ArrowUpDown className="w-5 h-5 text-green-600" /> Apply Replenishment List</DialogTitle>
            <DialogDescription>Execute this replenishment plan as real inventory transfers.</DialogDescription>
          </DialogHeader>
          {applyTarget && (
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-3 p-3 bg-muted/50 rounded-lg">
                <div><p className="text-xs text-muted-foreground">List</p><p className="font-medium text-sm">{applyTarget.location_name}</p></div>
                <div><p className="text-xs text-muted-foreground">Items / Qty</p><p className="font-medium text-sm">{applyTarget.total_items} items, {applyTarget.total_suggested_qty} units</p></div>
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Source Warehouse</label>
                <SearchableSelect options={warehouses.map(w => ({ value: w.id, label: w.name }))} value={applyWarehouseId} onValueChange={setApplyWarehouseId} placeholder="Select source warehouse..." emptyText="No warehouses found" />
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Destination Vehicle</label>
                <SearchableSelect options={vehicles.map(v => ({ value: v.id, label: v.name }))} value={applyVanId} onValueChange={setApplyVanId} placeholder="Select destination vehicle..." emptyText="No vehicles found" />
              </div>
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
                <p className="font-medium">This action will mutate inventory.</p>
                <p className="mt-0.5">Stock will be deducted from the warehouse and added to the vehicle.</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setApplyTarget(null)}>Cancel</Button>
            <Button onClick={handleApply} disabled={applying || !applyWarehouseId || !applyVanId} className="bg-green-600 hover:bg-green-700" data-testid="apply-confirm-btn">
              {applying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ArrowUpDown className="w-4 h-4 mr-2" />} Apply Transfers
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

// ──────────────────────────────────────────────
// Main Consolidated Replenishment Page
// ──────────────────────────────────────────────
export const Replenishment = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get('view') === 'needs-attention' ? 'needs-attention' : (searchParams.get('view') || 'needs-attention');

  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [warehouses, setWarehouses] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [attentionCount, setAttentionCount] = useState(0);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [listsRes, whRes, vansRes] = await Promise.all([
        replenishmentAPI.list({ limit: 50 }),
        warehouseAPI.listWarehouses(),
        vansAPI.list({ include_archived: false }),
      ]);
      setLists(listsRes.data || []);
      setWarehouses(whRes.data || []);
      setVehicles(vansRes.data || []);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (value) => {
    setSearchParams(value === 'needs-attention' ? {} : { view: value }, { replace: true });
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <Package className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold" data-testid="replenishment-title">Replenishment</h1>
              <p className="text-sm text-muted-foreground">Low stock alerts, restock actions, and replenishment planning</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue={initialTab} onValueChange={handleTabChange}>
          <TabsList className="w-full md:w-auto" data-testid="replenishment-tabs">
            <TabsTrigger value="needs-attention" className="gap-1.5" data-testid="tab-needs-attention">
              <AlertTriangle className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Needs Attention</span>
              <span className="sm:hidden">Attention</span>
              {attentionCount > 0 && <Badge variant="destructive" className="ml-1 text-[10px] px-1.5 py-0">{attentionCount}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="predictions" className="gap-1.5" data-testid="tab-predictions">
              <TrendingUp className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Predictions</span>
              <span className="sm:hidden">Predict</span>
            </TabsTrigger>
            <TabsTrigger value="saved-lists" className="gap-1.5" data-testid="tab-saved-lists">
              <FileSpreadsheet className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Saved Lists</span>
              <span className="sm:hidden">Lists</span>
              {lists.length > 0 && <Badge variant="secondary" className="ml-1 text-[10px] px-1.5 py-0">{lists.length}</Badge>}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="needs-attention">
            <NeedsAttentionPanel onCountChange={setAttentionCount} />
          </TabsContent>

          <TabsContent value="predictions">
            <PredictionPanel warehouses={warehouses} vehicles={vehicles} />
          </TabsContent>

          <TabsContent value="saved-lists">
            <SavedListsPanel lists={lists} loading={loading} warehouses={warehouses} vehicles={vehicles} onRefresh={loadData} />
          </TabsContent>
        </Tabs>
      </div>

      <MobileFAB onClick={() => {}} data-testid="mobile-fab-replenishment" />
    </Layout>
  );
};

export default Replenishment;
